// ** Checks if an object is empty (returns boolean)
import moment from 'moment';
import { forEach, invert, keys, map } from 'lodash-es';
import * as XLSX from 'xlsx';
import { COLUMN_PARAMS } from '@src/constants/columns';

export const isObjEmpty = (obj) => Object.keys(obj).length === 0;

// ** Returns K format from a number
export const kFormatter = (num) => (num > 999 ? `${(num / 1000).toFixed(1)}k` : num);

// ** Converts HTML to string
export const htmlToString = (html) => html.replace(/<\/?[^>]+(>|$)/g, '');

// ** Checks if the passed date is today
const isToday = (date) => {
  const today = new Date();
  return (
    /* eslint-disable operator-linebreak */
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
    /* eslint-enable */
  );
};

/**
 ** Format and return date in Humanize format
 ** Intl docs: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/format
 ** Intl Constructor: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat
 * @param {String} value date to format
 * @param {Object} formatting Intl object to format with
 */
export const formatDate = (value, formatting = { month: 'short', day: 'numeric', year: 'numeric' }) => {
  if (!value) return value;
  return new Intl.DateTimeFormat('en-US', formatting).format(new Date(value));
};

// ** Returns short month of passed date
export const formatDateToMonthShort = (value, toTimeForCurrentDay = true) => {
  const date = new Date(value);
  let formatting = { month: 'short', day: 'numeric' };

  if (toTimeForCurrentDay && isToday(date)) {
    formatting = { hour: 'numeric', minute: 'numeric' };
  }

  return new Intl.DateTimeFormat('en-US', formatting).format(new Date(value));
};

/**
 ** Return if user is logged in
 ** This is completely up to you and how you want to store the token in your frontend application
 *  ? e.g. If you are using cookies to store the application please update this function
 */
export const isUserLoggedIn = () => localStorage.getItem('userData');
export const getUserData = () => JSON.parse(localStorage.getItem('userData'));

/**
 ** This function is used for demo purpose route navigation
 ** In real app you won't need this function because your app will navigate to same route for each users regardless of ability
 ** Please note role field is just for showing purpose it's not used by anything in frontend
 ** We are checking role just for ease
 * ? NOTE: If you have different pages to navigate based on user ability then this function can be useful. However, you need to update it.
 * @param {String} userRole Role of user
 */
export const getHomeRouteForLoggedInUser = (userRole) => {
  if (userRole === 'admin') return '/';
  if (userRole === 'client') return '/access-control';
  return '/login';
};

// ** React Select Theme Colors
export const selectThemeColors = (theme) => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary25: '#7367f01a', // for option hover bg-color
    primary: '#7367f0', // for selected option bg-color
    neutral10: '#7367f0', // for tags bg-color
    neutral20: '#ededed', // for input border-color
    neutral30: '#ededed', // for input hover border-color
  },
});

export const removeAtIndex = (arr, index) => {
  const copy = [...arr];
  copy.splice(index, 1);
  return copy;
};

export const toggleArray = (arr, item, getValue = (item) => item) => {
  const index = arr.findIndex((i) => getValue(i) === getValue(item));
  if (index === -1) return [...arr, item];
  return removeAtIndex(arr, index);
};

export const dateArrayToDateString = (dateOptionValue) => {
  const [start_date, end_date] = dateOptionValue;
  return `${moment(start_date).format('YYYY-MM-DD')}~${moment(end_date).format('YYYY-MM-DD')}`;
};

export const dateStringToDateArray = (data) => data?.split?.('~') || [];

export const downloadExcelByBlob = (data, filename) => {
  const fileBlob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const objectURL = window.URL.createObjectURL(fileBlob);
  const exportLinkElement = document.createElement('a');
  exportLinkElement.hidden = true;
  exportLinkElement.download = `${filename}.xlsx`;
  exportLinkElement.href = objectURL;
  document.body.appendChild(exportLinkElement);
  exportLinkElement.click();
  exportLinkElement.remove();
};

export const scrollIntoErrorView = (errors) => {
  const elements = keys(errors).map((name) => document.getElementsByName(name)[0]).filter((el) => !!el);
  elements.sort((a, b) => b.scrollHeight - a.scrollHeight);
  elements[0]?.scrollIntoView({ block: 'center' });
};

export const readExcelFile = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  // eslint-disable-next-line func-names
  reader.onload = function () {
    const data = reader.result;
    const workBook = XLSX.read(data, { type: 'binary' });
    workBook.SheetNames.forEach((sheetName) => {
      const rows = XLSX.utils.sheet_to_json(workBook.Sheets[sheetName], { defval: '', raw: false });
      console.log(`SheetName: ${sheetName}`, rows);
      resolve(rows);
    });
  };
  // eslint-disable-next-line func-names
  reader.onerror = function (event) {
    reject(event);
  };
  reader.readAsBinaryString(file);
});

export const composeExcelRowsToColumns = (rows) => {
  const invertColumnParams = invert(COLUMN_PARAMS);
  return rows.map((item) => {
    const clonedItem = {};
    forEach(item, (value, key) => {
      clonedItem[invertColumnParams[key]] = value;
    });
    return clonedItem;
  });
};

export const getTextByte = (string = '') => {
  if (typeof string !== 'string') return 0;
  // eslint-disable-next-line func-names
  const byte = (function (s, b, i, c) {
    // eslint-disable-next-line
    for (b = i = 0; c = s.charCodeAt(i++); b += c >> 11 ? 3 : c >> 7 ? 2 : 1);
    return b;
  }(string));
  return byte;
};

export const isValidByteText = (string, limit) => {
  const byte = getTextByte(string);
  return (byte <= limit);
};

export const categoryToOption = (props) => ({ label: props?.name, value: props?.category_code, ...props });

export const getTableDataSource = (data = []) => map(data, (item) => ({
  key: item.id,
  ...item,
})) || [];

export const closeTab = () => {
  window.opener = null;
  window.open('', '_self');
  window.close();
};
